// A registered Gao product, as a card.
//
// The registry used to render as a seven-column table whose first two columns
// were `productId` and `displayName` and whose middle four were a revision id,
// an ISO timestamp, an operator id and a 64-character document hash — four
// machine values in the widest columns, before the operator reached the one
// thing they came for, which is whether the product is live and what to do
// about it.
//
// The card keeps every one of those values. It reorders them by who needs
// them: the product's name and state first, the revision and publication time
// as a supporting line, and the document hash under `title` on the revision
// chip rather than occupying a column. Nothing is dropped — `ControlPlaneProduct`
// renders the full hash on the detail page.

import { Link } from "react-router-dom";
import type { ProductRow } from "../controlPlane/api";
import { Chip } from "./primitives";
import { productState } from "./productState";
import { IconArrowRight, IconProducts } from "./icons";

function shortTime(iso: string | null): string {
  if (!iso) return "never";
  // Date-only. The console renders full ISO timestamps in the detail view and
  // the audit trail; a card does not need seconds to answer "is this recent".
  return iso.slice(0, 10);
}

export function ProductCard({ product, lane }: { product: ProductRow; lane?: string }) {
  const state = productState(product);
  return (
    <Link
      to={`/control-plane/${product.productId}`}
      className="g-tile"
      style={{ minHeight: 186 }}
      aria-label={`${product.displayName} — ${state.label}`}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: "var(--r-md)",
            background: "var(--accent-bg)",
            border: "1px solid var(--accent-border)",
            color: "var(--accent)",
            flex: "0 0 auto",
          }}
        >
          <IconProducts size={20} />
        </span>
        <span style={{ flex: "1 1 auto" }} />
        <Chip tone={state.tone} solid>
          {state.label}
        </Chip>
      </div>

      <div style={{ marginTop: 10, fontSize: 15.5, fontWeight: 640, color: "var(--fg)" }}>
        {product.displayName}
      </div>
      <code className="g-mono" style={{ fontSize: 11, padding: "1px 6px", alignSelf: "flex-start" }}>
        {product.productId}
      </code>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          fontSize: 11.5,
          color: "var(--fg-dim)",
        }}
      >
        {lane !== undefined && <Chip size="sm">{lane.toUpperCase()}</Chip>}
        <Chip size="sm" title={product.documentHash ?? undefined}>
          {product.currentRevisionId === null
            ? "no revision"
            : `rev ${product.pointerSeq ?? "?"}`}
        </Chip>
        <Chip size="sm">published {shortTime(product.publishedAt)}</Chip>
      </div>

      <div
        style={{
          marginTop: "auto",
          paddingTop: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12.5,
          fontWeight: 600,
          color: "var(--accent)",
        }}
      >
        Manage
        <IconArrowRight size={14} />
      </div>
    </Link>
  );
}
